import {Linking, Platform} from 'react-native'
import {AtUri, type AppBskyFeedDefs} from '@atproto/api'
import * as FileSystem from 'expo-file-system'
import * as IntentLauncher from 'expo-intent-launcher'

import {FACEBOOK_APP_ID} from '#/env'
import {makeProfileLink} from '#/lib/routes/links'
import {toShareUrl} from '#/lib/strings/url-helpers'
import {logger} from '#/logger'
import {isAndroid, isIOS, isWeb} from '#/platform/detection'
import {
  getCachedShareCard,
  renderShareCardToPngUri,
} from '#/lib/sharing/renderShareCard'
import {InstagramStories} from '../../../modules/expo-bluesky-swiss-army'

export type TellusPost = AppBskyFeedDefs.PostView

export const INSTAGRAM_STORY_ACTION = 'com.instagram.share.ADD_TO_STORY'
export const INSTAGRAM_STORIES_URL = 'instagram-stories://share'
export const INSTAGRAM_PASTEBOARD_KEYS = {
  backgroundImage: 'com.instagram.sharedSticker.backgroundImage',
  contentUrl: 'com.instagram.sharedSticker.contentURL',
}

type ShareResult = {ok: boolean; reason?: string}

export function buildInstagramStoryPayload({
  imageUri,
  contentUrl,
  sourceApplication,
}: {
  imageUri: string
  contentUrl: string
  sourceApplication: string
}) {
  return {
    ios: {
      pasteboardItems: {
        [INSTAGRAM_PASTEBOARD_KEYS.backgroundImage]: imageUri,
        [INSTAGRAM_PASTEBOARD_KEYS.contentUrl]: contentUrl,
      },
      sourceApplication,
    },
    android: {
      intentAction: INSTAGRAM_STORY_ACTION,
      extras: {
        interactive_asset_uri: imageUri,
        content_url: contentUrl,
        source_application: sourceApplication,
      },
    },
  }
}

export async function sharePostToInstagramStory(
  post: TellusPost,
): Promise<ShareResult> {
  const platform =
    Platform.OS === 'ios' ||
    Platform.OS === 'android' ||
    Platform.OS === 'web'
      ? Platform.OS
      : 'unknown'
  logger.metric(
    'share_instagram_story_attempt',
    {status: 'attempt', platform},
    {statsig: true},
  )

  if (isWeb) {
    logger.metric(
      'share_instagram_story_attempt',
      {status: 'failure', reason: 'web', platform},
      {statsig: true},
    )
    return {ok: false, reason: 'web'}
  }

  if (!FACEBOOK_APP_ID) {
    logger.metric(
      'share_instagram_story_attempt',
      {status: 'failure', reason: 'missing_facebook_app_id', platform},
      {statsig: true},
    )
    return {ok: false, reason: 'missing_facebook_app_id'}
  }

  try {
    const contentUrl = buildPostSummary(post)
    const shareCard =
      getCachedShareCard(post) ?? (await renderShareCardToPngUri(post))

    if (isIOS) {
      if (typeof Platform.Version === 'number' && Platform.Version < 10) {
        logger.metric(
          'share_instagram_story_attempt',
          {status: 'failure', reason: 'unsupported_ios_version', platform},
          {statsig: true},
        )
        return {ok: false, reason: 'unsupported_ios_version'}
      }

      const canOpen = await Linking.canOpenURL(INSTAGRAM_STORIES_URL)
      if (!canOpen) {
        logger.metric(
          'share_instagram_story_attempt',
          {status: 'failure', reason: 'not_installed', platform},
          {statsig: true},
        )
        return {ok: false, reason: 'not_installed'}
      }

      const imageBase64 = await readImageAsBase64(shareCard.uri)

      await InstagramStories.setStoryPasteboard(
        imageBase64,
        contentUrl,
        5 * 60 * 1000,
      )

      await Linking.openURL(
        `${INSTAGRAM_STORIES_URL}?source_application=${encodeURIComponent(
          FACEBOOK_APP_ID,
        )}`,
      )

      logger.metric(
        'share_instagram_story_attempt',
        {status: 'success', platform},
        {statsig: true},
      )
      return {ok: true}
    }

    if (isAndroid) {
      const instagramAvailable = await isInstagramAvailableAndroid()
      if (!instagramAvailable) {
        logger.metric(
          'share_instagram_story_attempt',
          {status: 'failure', reason: 'not_installed', platform},
          {statsig: true},
        )
        return {ok: false, reason: 'not_installed'}
      }

      const contentUri = await FileSystem.getContentUriAsync(shareCard.uri)
      const payload = buildInstagramStoryPayload({
        imageUri: contentUri,
        contentUrl,
        sourceApplication: FACEBOOK_APP_ID,
      })

      await IntentLauncher.startActivityAsync(payload.android.intentAction, {
        data: contentUri,
        type: 'image/*',
        flags: 1,
        packageName: 'com.instagram.android',
        extra: payload.android.extras,
      })

      logger.metric(
        'share_instagram_story_attempt',
        {status: 'success', platform},
        {statsig: true},
      )
      return {ok: true}
    }

    logger.metric(
      'share_instagram_story_attempt',
      {status: 'failure', reason: 'unsupported_platform', platform},
      {statsig: true},
    )
    return {ok: false, reason: 'unsupported_platform'}
  } catch (error: any) {
    logger.error('Failed to share to Instagram Stories', {message: error})
    logger.metric(
      'share_instagram_story_attempt',
      {
        status: 'failure',
        reason: error?.message || 'unknown_error',
        platform,
      },
      {statsig: true},
    )
    return {ok: false, reason: 'error'}
  }
}

function buildPostSummary(post: TellusPost) {
  const postAuthor = post.author
  const urip = new AtUri(post.uri)
  const href = makeProfileLink(postAuthor, 'post', urip.rkey)
  const url = toShareUrl(href)
  return url
}

async function readImageAsBase64(uri: string) {
  if (uri.startsWith('data:')) {
    const [, base64] = uri.split('base64,')
    return base64 || ''
  }
  return await FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  })
}

async function isInstagramAvailableAndroid() {
  try {
    const icon = await IntentLauncher.getApplicationIconAsync(
      'com.instagram.android',
    )
    return Boolean(icon)
  } catch {
    return false
  }
}
